import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEstadoRenovacion, getPersonaChipClass } from '../../../../utils/styles.js';
import { useAuth } from '../../../hooks/useAuth'; 

// NUESTROS COMPONENTES DE ARQUITECTURA MODULAR COMUNES
import SortableHeader from '../common/SortableHeader';
import PaginatorFooter from '../common/PaginatorFooter';
import ColumnSelector from '../common/ColumnSelector';
import TableContainer from '../common/TableContainer';
import Icons from '../common/Icons';

// MODALES EXISTENTES EN EL PROYECTO
import ModalDetallePoliza from '../../../ui/ModalDetallePoliza.jsx';
import EditarRenovacion from '../../../ui/EditarRenovacion.jsx';
import RenovarPoliza from '../../../ui/RenovarPoliza.jsx';
import ModalDetalleDeuda from '../../../ui/ModalDetalleDeuda.jsx';
import ModalFichaCliente from '../../../ui/ModalFichaCliente.jsx';

// FILTROS Y CONTROLADOR
import FiltrosDinamicos from '../../../ui/FiltrosDinamicos.jsx';
import { usePeriodoCortoTable } from './usePeriodoCortoTable';

// --- FILA MEMOIZADA ---
const TableRow = memo(({ p, isSelected, isLocked, isLockedByMe, columnVisibility, onSelect, onAction, onUnlock, onShowDebt }) => {
    const today = useMemo(() => {
        const d = new Date();
        d.setUTCHours(0, 0, 0, 0);
        return d;
    }, []);

    const { dateClass, formattedDate } = useMemo(() => {
        if (!p.vencimientoDate) return { dateClass: '', formattedDate: 'N/A' };
        const vDate = new Date(p.vencimientoDate + "T00:00:00Z");
        const isVencida = vDate < today;
        return {
            dateClass: isVencida ? 'text-red-600 font-bold' : 'text-blue-700 font-semibold',
            formattedDate: p.vencimientoDate.split('-').reverse().join('/')
        };
    }, [p.vencimientoDate, today]);

    return (
        <tr 
            onClick={() => !isLocked && onSelect(p.id)}
            className={`group cursor-pointer transition-all duration-75 border-b border-b border-slate-100
                ${isLocked ? 'opacity-40 select-none bg-slate-50' : ''} 
                ${isSelected ? 'bg-blue-100/60' : 'hover:bg-slate-50'}`}
        >
            {columnVisibility.vencimientoDate && (
                <td className={`p-2.5 px-3 text-sm whitespace-nowrap border-b border-r border-slate-100 text-center ${dateClass}`}>
                    {formattedDate}
                </td>
            )}

            {columnVisibility.nPoliza && (
                <td className="p-2.5 px-3 text-sm text-slate-900 font-bold text-center border-b border-r border-slate-100 tabular-nums">
                    {p.nPoliza}
                </td>
            )}

            {columnVisibility.compania && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                    <div className="flex items-center justify-center">
                        <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-100 rounded-lg w-fit group-hover:bg-white transition-colors shadow-sm">
                            <div className="text-blue-400"><Icons.Company /></div>
                            <span className="text-[11px] text-blue-800 font-bold uppercase truncate max-w-[160px]">{p.compania}</span>
                        </div>
                    </div>
                </td>
            )}

            {columnVisibility.displayNombre && (
                <td className="p-2.5 px-4 text-sm text-slate-900 font-bold whitespace-nowrap truncate border-b border-r border-slate-100 text-left" title={p.displayNombre}>
                    <div className="flex items-center justify-between gap-4">
                        <span className="truncate">{p.displayNombre}</span>
                        <div className="flex items-center gap-1.5 shrink-0">
                            {p.tieneDeuda && (
                                <div onClick={(e) => { e.stopPropagation(); onShowDebt(p); }} className="flex items-center text-red-600 animate-pulse cursor-pointer transition-transform p-0.5 rounded-full hover:bg-red-50" title="Ver Deuda">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                                </div>
                            )}
                        </div>
                    </div>
                </td>
            )}

            {columnVisibility.displayDoc && (
                <td className="p-2.5 px-3 text-sm text-slate-600 font-mono text-center border-b border-r border-slate-100">{p.displayDoc}</td>
            )}

            {columnVisibility.riesgo && (
                <td className="p-2.5 px-4 text-sm text-slate-700 whitespace-nowrap border-b border-r border-slate-100">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex flex-col truncate text-left">
                            <span className="font-bold text-slate-800 tracking-tight text-left">{p.riesgo}</span>
                            <span className="text-[11px] text-slate-500 font-medium uppercase truncate mt-0.5 text-left">{p.subriesgo || 'General'}</span>
                        </div>
                        <div className="shrink-0">
                            {p.formaRenovacion === 'AUTOMÁTICA' ? (
                                <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shadow-sm border border-emerald-200" title="Renovación Automática"><svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" /></svg></div>
                            ) : (
                                <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center shadow-sm border border-slate-200" title="Renovación Manual"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11" /></svg></div>
                            )}
                        </div>
                    </div>
                </td>
            )}

            {columnVisibility.formaPago && (
                <td className="p-2.5 px-3 border-b border-r border-slate-100 text-center">
                    <div className="flex items-center justify-center px-3 h-[26px] bg-blue-50 border border-blue-100 rounded-lg w-fit mx-auto">
                        <span className="text-[11px] text-blue-800 font-bold uppercase">{p.formaPago || '-'}</span>
                    </div>
                </td>
            )}

            {columnVisibility.estadoGestion && (
                <td className="p-2.5 px-3 text-center border-b border-r border-slate-100">
                    <span className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase w-fit ${getEstadoRenovacion(p.estadoGestionRenovacion || 'PENDIENTE')}`}>
                        {p.estadoGestionRenovacion || 'PENDIENTE'}
                    </span>
                </td>
            )}

            {columnVisibility.vendedor && (
                <td className="p-2.5 px-3 text-sm text-slate-900 font-bold text-center border-b border-r border-slate-100 tabular-nums">
                    {p.vendedor || '-'}
                </td>
            )}

            {columnVisibility.gestionadoPor && (
                <td className="p-2.5 px-3 text-center border-b border-r border-slate-100">
                    {p.gestionadoPor ? (
                        <div className={`flex items-center gap-2 px-2.5 py-1 rounded-full w-fit shadow-sm border border-black/5 mx-auto ${getPersonaChipClass(p.gestionadoPor)}`}>
                            <Icons.User />
                            <span className="text-[10px] font-bold uppercase">{p.gestionadoPor}</span>
                        </div>
                    ) : <span className="text-slate-200 text-sm font-bold">-</span>}
                </td>
            )}

            {columnVisibility.canal && (
                <td className="p-2.5 px-3 text-sm text-slate-700 whitespace-nowrap border-b border-r border-slate-100 text-left">
                    <div className="flex flex-col truncate text-left">
                        <span className="font-bold text-slate-800 tracking-tight text-left">{p.canal}</span>
                        <span className="text-[11px] text-slate-500 font-medium uppercase truncate mt-0.5 text-left">{p.canalNombre || p.subcanal || '-'}</span>
                    </div>
                </td>
            )}

            {/* Acciones */}
            <td className="p-2.5 px-4 text-right sticky right-0 transition-colors z-20 border-b border-l border-slate-100 shadow-[-10px_0_15px_-5px_rgba(0,0,0,0.03)] bg-white group-hover:bg-slate-50">
                <div className="flex justify-end items-center gap-2">
                    <button type="button" onClick={(e) => { e.stopPropagation(); onAction(p, 'ficha'); }} className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all active:scale-90" title="Ficha Técnica Cliente"><Icons.User /></button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); onAction(p, 'view'); }} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all active:scale-90" title="Ver Detalles"><Icons.Policy /></button>
                    {isLocked ? (
                        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-100 animate-pulse shadow-sm">
                            <Icons.Lock /> <span className="text-[9px] font-black uppercase tracking-tighter">{p.bloqueo?.userName || 'Ocupado'}</span>
                        </div>
                    ) : (
                        <>
                            <button type="button" onClick={(e) => { e.stopPropagation(); onAction(p, 'edit'); }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all active:scale-90" title="Gestionar Renovación"><Icons.Edit /></button>
                            <button type="button" onClick={(e) => { e.stopPropagation(); onAction(p, 'renew'); }} className="p-1.5 text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all active:scale-90" title="Renovar Póliza"><Icons.ChevronRight /></button>
                            {isLockedByMe && (
                                <button type="button" onClick={(e) => { e.stopPropagation(); onUnlock(p.path); }} className="p-1.5 text-red-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Liberar Caso">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                                </button>
                            )}
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
});
TableRow.displayName = 'TableRow';

// --- ORQUESTADOR VISUAL MÁSTER ---
const GestionPeriodoCorto = ({ onRenovacionSuccess }) => {
    const navigate = useNavigate();
    const { user } = useAuth();

    // Consumimos nuestro cerebro de negocio estructurado
    const {
        loading,
        needsHydration,
        clientsCache,
        activePoliza,
        showDetailModal,
        showEditModal,
        showRenewModal,
        showDebtModal,
        showFichaModal,
        tableState,
        itemsTable,
        activeItemPathRef,
        setShowDetailModal,
        setShowEditModal,
        setShowRenewModal,
        setShowDebtModal,
        setShowFichaModal,
        setActivePoliza,
        handleAction,
        handleShowDebt,
        unlockPoliza
    } = usePeriodoCortoTable();

    // Desestructuramos el estado de la tabla
    const {
        sortConfig,
        columnVisibility,
        currentPage,
        setCurrentPage,
        selectedRowId,
        setSelectedRowId,
        filterValues,
        filteredData,
        paginatedData,
        totalPages,
        handleSort,
        toggleColumn,
        handleFilterChange,
        checkIsLocked: checkIsItemLocked
    } = tableState;

    const columnLabels = {
        vencimientoDate: "Vencimiento", nPoliza: "N° Póliza", compania: "Compañía",
        displayNombre: "Cliente", displayDoc: "DNI/CUIT", riesgo: "Riesgo/Subr", formaPago: "Pago",
        estadoGestion: "Estado", gestionadoPor: "Resp.", canal: "Canal", vendedor: "Vendedor"
    };

    // Liberación automática al desmontar pestaña
    useEffect(() => {
        const handleUnload = () => { if (activeItemPathRef.current) unlockPoliza(activeItemPathRef.current); };
        window.addEventListener('beforeunload', handleUnload);
        return () => window.removeEventListener('beforeunload', handleUnload);
    }, [unlockPoliza, activeItemPathRef]);

    // Configuración de Filtros Dinámicos
    const filterConfig = useMemo(() => {
        const getUnique = (list, key) => [...new Set(list.map(p => p[key]))].filter(Boolean).sort().map(v => ({label: v, value: v}));
        
        const selCanal = filterValues.canal || [];
        const dataForCanalNombre = itemsTable.filter(d => selCanal.length === 0 || selCanal.includes(d.canal));
        const canalNombreOptions = [...new Set(dataForCanalNombre.map(d => d.canalNombre || d.subcanal))].filter(Boolean).sort().map(v => ({label: v, value: v}));
        const selCanalNombre = filterValues.canalNombre || [];
        const dataForSubcanal = dataForCanalNombre.filter(d => selCanalNombre.length === 0 || selCanalNombre.includes(d.canalNombre || d.subcanal));
        const subcanalOptions = getUnique(dataForSubcanal, 'subcanal');
        const vendedorNombreOptions = getUnique(itemsTable, 'vendedor');

        return [
            { name: 'search', label: 'Buscador', type: 'text', placeholder: 'Cliente o Póliza...', className: 'w-72' },
            { name: 'riesgo', label: 'Riesgo', type: 'select', isMulti: true, options: getUnique(itemsTable, 'riesgo') },
            { name: 'subriesgo', label: 'Subriesgo', type: 'select', isMulti: true, options: getUnique(itemsTable, 'subriesgo') },
            { name: 'compania', label: 'Compañía', type: 'select', isMulti: true, options: getUnique(itemsTable, 'compania') },
            { name: 'formaPago', label: 'Forma Pago', type: 'select', isMulti: true, options: getUnique(itemsTable, 'formaPago') },
            { name: 'estadoGestion', label: 'Estado', type: 'select', isMulti: true, options: getUnique(itemsTable, 'estadoGestion') },
            { name: 'vendedor', label: 'Vendedor', type: 'select', isMulti: true, options: vendedorNombreOptions },
            { name: 'gestionadoPor', label: 'Responsable', type: 'select', isMulti: true, options: getUnique(itemsTable, 'gestionadoPor') },
            { name: 'canal', label: 'Canal', type: 'select', isMulti: true, options: getUnique(itemsTable, 'canal') },
            { name: 'canalNombre', label: 'Nombre Canal', type: 'select', isMulti: true, options: canalNombreOptions },
            { name: 'subcanal', label: 'Subcanal / Asociado', type: 'select', isMulti: true, options: subcanalOptions },
            { name: 'vencimientoDate', label: 'Vencimiento', type: 'date-range', className: 'w-64' },
        ];
    }, [itemsTable, filterValues]);

    // ◄ PREVENCIÓN DE PARPADEO (needsHydration)
    if (loading || needsHydration) return (
        <div className="flex flex-col items-center justify-center h-screen bg-white space-y-6">
            <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
            <div className="text-center">
                <span className="font-black text-[10px] uppercase tracking-[0.3em] text-indigo-900 animate-pulse">Sincronizando Filtros Dinámicos</span>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col h-screen w-full bg-white text-slate-900 overflow-hidden antialiased font-['Inter',_sans-serif] text-left">
            <div className="h-1 bg-indigo-600 w-full shrink-0"></div>
            
            {/* CABECERA UNIFICADA */}
            <header className="flex h-20 shrink-0 items-center justify-between border-b border-slate-200 px-8 bg-white z-[110]">
                <div className="flex items-center gap-6">
                    <button type="button" onClick={() => navigate('/cpanel')} className="p-2 text-slate-400 hover:text-slate-800 hover:bg-slate-50 rounded-lg transition-all border border-transparent hover:border-slate-200 shadow-sm"><Icons.ArrowLeft /></button>
                    <div className="text-left">
                        <h2 className="text-xl font-bold text-slate-800 tracking-tight leading-tight uppercase">Renovaciones Especiales</h2>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest text-left">Vtos: -15d a +60d (Periodo Corto / Temporales)</p>
                    </div>
                </div>
                <div className="flex items-center gap-4 text-left">
                    <ColumnSelector 
                        columnVisibility={columnVisibility} 
                        columnLabels={columnLabels} 
                        toggleColumn={toggleColumn} 
                    />
                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-1 pr-4 shadow-sm">
                        <div className="bg-indigo-600 text-white px-4 py-1.5 rounded-lg text-lg font-black">{filteredData.length}</div>
                        <span className="text-[10px] font-bold uppercase text-slate-500">Casos</span>
                    </div>
                </div>
            </header>

            {/* FILTROS DINÁMICOS CON KEY ESTABLE */}
            <FiltrosDinamicos 
                key="periodocorto-table-filters"
                config={filterConfig} 
                onFilterChange={handleFilterChange} 
                defaultValues={filterValues} 
            />

            {/* TABLA PRINCIPAL RESPONSIVE */}
            <main className="flex-grow overflow-hidden flex flex-col bg-white text-left">
                <TableContainer minWidth="2000px">
                    <thead>
                        <tr className="bg-[#01032d] text-white">
                            <SortableHeader label="Vencimiento" sortKey="vencimientoDate" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="N° Póliza" sortKey="nPoliza" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Compañía" sortKey="compania" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-48 text-center" />
                            <SortableHeader label="Cliente" sortKey="displayNombre" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[320px] text-left" />
                            <SortableHeader label="DNI / CUIT" sortKey="displayDoc" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-32 text-center" />
                            <SortableHeader label="Riesgo / Subriesgo" sortKey="riesgo" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-[260px] text-left" />
                            <SortableHeader label="Forma de Pago" sortKey="formaPago" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-44 text-center" />
                            <SortableHeader label="Estado" sortKey="estadoGestion" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-44 text-center" />
                            <SortableHeader label="Vendedor" sortKey="vendedor" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-44 text-center" />
                            <SortableHeader label="Responsable" sortKey="gestionadoPor" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-44 text-center" />
                            <SortableHeader label="Canal" sortKey="canal" sortConfig={sortConfig} columnVisibility={columnVisibility} onSort={handleSort} className="w-48 text-left" />
                            <th className="w-48 p-4 sticky right-0 top-0 z-[65] bg-slate-900 border-b border-slate-700 uppercase tracking-wider text-[10px] font-semibold text-center text-white shadow-[-2px_0_5px_rgba(0,0,0,0.2)]">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-left">
                        {paginatedData.map(item => {
                            const isLocked = checkIsItemLocked(item, user?.uid);
                            const isLockedByMe = item.bloqueo && item.bloqueo.userId === user?.uid;

                            return (
                                <TableRow 
                                    key={item.id} 
                                    p={item} 
                                    isSelected={selectedRowId === item.id} 
                                    isLocked={isLocked} 
                                    isLockedByMe={isLockedByMe} 
                                    columnVisibility={columnVisibility} 
                                    onSelect={setSelectedRowId} 
                                    onAction={handleAction} 
                                    onUnlock={unlockPoliza} 
                                    onShowDebt={handleShowDebt} 
                                />
                            );
                        })}
                    </tbody>
                </TableContainer>

                {/* PIE DE PAGINACIÓN COMÚN (Acento azul corporativo de Periodo Corto) */}
                <PaginatorFooter 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    currentLength={paginatedData.length}
                    totalLength={filteredData.length}
                    onPageChange={setCurrentPage}
                    label="resultados"
                    accentClass="text-blue-900 bg-blue-50 border-blue-100"
                />
            </main>

            {/* MODALES RECONECTADOS */}
            <ModalDetalleDeuda show={showDebtModal} onClose={() => setShowDebtModal(false)} poliza={activePoliza} client={activePoliza ? { id: activePoliza.clienteId, ...(clientsCache[activePoliza.clienteId] || {}) } : null} />
            <ModalDetallePoliza show={showDetailModal} onClose={() => { setShowDetailModal(false); setActivePoliza(null); }} poliza={activePoliza} />
            <ModalFichaCliente show={showFichaModal} onClose={() => { setShowFichaModal(false); setActivePoliza(null); }} client={activePoliza ? { id: activePoliza.clienteId, ...(clientsCache[activePoliza.clienteId] || {}) } : null} />
            
            {activePoliza && (
                <>
                    <EditarRenovacion 
                        show={showEditModal} 
                        onClose={() => { unlockPoliza(activePoliza.path); setShowEditModal(false); setActivePoliza(null); }} 
                        poliza={activePoliza} 
                        freshClientData={clientsCache[activePoliza.clienteId] || { id: activePoliza.clienteId }} 
                        onSuccess={() => { unlockPoliza(activePoliza.path); setShowEditModal(false); setActivePoliza(null); }} 
                    />
                    <RenovarPoliza 
                        show={showRenewModal} 
                        onClose={() => { unlockPoliza(activePoliza.path); setShowRenewModal(false); setActivePoliza(null); }} 
                        poliza={activePoliza} 
                        freshClientData={clientsCache[activePoliza.clienteId] || { id: activePoliza.clienteId }} 
                        onSuccess={() => { unlockPoliza(activePoliza.path); onRenovacionSuccess?.(activePoliza); setShowRenewModal(false); setActivePoliza(null); }} 
                    />
                </>
            )}
        </div>
    );
};

export default GestionPeriodoCorto;